/* eslint-disable max-lines */
import tokens from '@kluein/design-tokens';
import { Box, Text } from '@kluein/klue-ui';
import { NectarIcon } from '@kluein/nectar';
import {
  ArrowSquareOut,
  Bell,
  Cards,
  CaretDown,
  MagnifyingGlass,
  Sparkle,
} from '@phosphor-icons/react';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import useAuth from 'contexts/auth/useAuth';
import { useSearch } from 'contexts/ui/search-context';
import { useSearchParams } from 'hooks/search';
import { useDebounce } from 'hooks/use-debounce';
import { useAskKlue } from 'pages/dashboard/hooks';
import store from 'store';
import { setAskKlueInitialScreen } from 'store/views/dashboard';

import {
  StyledUnifiedSearchBox,
  StyledUnifiedSearchInput,
  StyledDropdownContainer,
  StyledDropdown,
  StyledDropdownItem,
  UNIFIED_SEARCH_EXPANDED_WIDTH,
} from './NavBarUnifiedSearch.styles';
import { UnifiedSearchSuggestions } from './partials';
import {
  useSearchSuggestionHandlers,
  createSuggestionItems,
} from './searchSuggestionHelpers';

import type { UnifiedSearchMode } from 'contexts/ui/search-context/searchContext.types';
import type { Dispatch } from 'store/store.types';

const NavBarUnifiedSearch = () => {
  const { t } = useTranslation(['Common', 'Search']);
  const { isCurator, visibilityGroup, isAskKlueEnabled, isWinLossExclusive } =
    useAuth();
  const { pathname } = useLocation();

  // Check if we're on ask-klue page and should be hidden
  const showingAskKluePage = pathname.includes('ask-klue');
  const shouldBeHidden = showingAskKluePage;

  const {
    search,
    context: searchContext,
    setContext,
    unifiedSearchMode,
    setUnifiedSearchMode,
  } = useSearch();
  const { openModal, handleAskKlue } = useAskKlue();
  const { terms } = useSearchParams();
  const navigate = useNavigate();

  // Direct search functions that bypass context state
  const searchCardsDirect = useCallback(
    (searchTerms: string) => {
      const path = searchTerms
        ? `/search/${encodeURIComponent(searchTerms)}`
        : '/search/';
      navigate(path);
    },
    [navigate],
  );

  const searchAlertsDirect = useCallback(
    (searchTerms: string) => {
      const path = searchTerms
        ? `/alerts?q=${encodeURIComponent(searchTerms)}`
        : '/alerts';
      navigate(path);
    },
    [navigate],
  );
  const [inputValue, setInputValue] = useState<string>(terms || '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [dropdownOffset, setDropdownOffset] = useState<number>(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout>();

  const {
    searchSuggestions: { getSuggestions, reset: resetSuggestions },
  } = useDispatch<Dispatch>();
  const suggestions = useSelector(store.select.searchSuggestions.suggestions);
  const debouncedInputValue = useDebounce(inputValue, 250);

  const onSuggestionsClose = useCallback(() => {
    setSuggestionOpen(false);
  }, []);

  const searchWrapper = useCallback(
    (options: {
      terms?: string;
      tagId?: number;
      tagOp?: string;
      reset?: boolean;
    }) => {
      search({
        terms: options.terms,
        tagId: options.tagId,
        tagOp: options.tagOp as 'add' | 'toggle',
        reset: options.reset,
      });
    },
    [search],
  );

  const { suggestionComponent } = useSearchSuggestionHandlers(
    onSuggestionsClose,
    setInputValue,
    resetSuggestions,
    searchWrapper,
  );

  // Handle click outside dropdown and suggestions to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setSuggestionOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Update search context when search type changes
  useEffect(() => {
    if (unifiedSearchMode === 'cards') {
      setContext('cards');
    } else if (unifiedSearchMode === 'alerts') {
      setContext('alerts');
    }
  }, [unifiedSearchMode, setContext]);

  // Handle search suggestions
  useEffect(() => {
    if (unifiedSearchMode === 'ask-klue') return;
    if (unifiedSearchMode === 'alerts') return;

    const searchTerms = debouncedInputValue?.trim();
    if (!searchTerms) {
      resetSuggestions();
    } else {
      getSuggestions({ searchTerms, visibilityGroup });
    }
  }, [
    debouncedInputValue,
    getSuggestions,
    resetSuggestions,
    unifiedSearchMode,
    searchContext,
    visibilityGroup,
  ]);

  const onSuggestionsOpen = () => !suggestionOpen && setSuggestionOpen(true);

  // Calculate dropdown position dynamically
  useEffect(() => {
    const calculateDropdownPosition = () => {
      if (searchBoxRef.current && dropdownRef.current) {
        const searchBoxRect = searchBoxRef.current.getBoundingClientRect();
        const containerRect = dropdownRef.current.getBoundingClientRect();
        const offset = searchBoxRect.left - containerRect.left;
        setDropdownOffset(offset);
      }
    };

    calculateDropdownPosition();

    // Recalculate on window resize
    const handleResize = () => {
      calculateDropdownPosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDropdownOpen]); // Recalculate when dropdown opens

  const suggestionItems = useMemo(() => {
    return createSuggestionItems(suggestions, suggestionComponent);
  }, [suggestions, suggestionComponent]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);

    if (unifiedSearchMode !== 'ask-klue') {
      !suggestionOpen && onSuggestionsOpen();
    }
  };

  const handleInputFocus = () => {
    if (unifiedSearchMode !== 'ask-klue') {
      onSuggestionsOpen();
    }
  };

  const handleInputBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => {
      setSuggestionOpen(false);
    }, 200);
  };

  const handleInputClick = () => {
    if (unifiedSearchMode === 'ask-klue' && isAskKlueEnabled) {
      // Blur the input to prevent aria-hidden conflict when modal opens
      if (inputRef.current) {
        inputRef.current.blur();
      }
      setAskKlueInitialScreen(true);
      openModal('chat');
    }
  };

  const handleSearchSubmit = useCallback(() => {
    // Close dropdown when search is started
    setIsDropdownOpen(false);

    if (unifiedSearchMode === 'ask-klue' && isAskKlueEnabled) {
      if (inputValue.trim()) {
        handleAskKlue(inputValue);
        setInputValue('');
      }
    } else {
      resetSuggestions();
      onSuggestionsClose();

      if (inputValue.trim()) {
        // Search with terms
        search({ terms: inputValue, reset: true });
      } else {
        // Clear search by navigating to base search page
        if (unifiedSearchMode === 'cards') {
          searchCardsDirect('');
        } else if (unifiedSearchMode === 'alerts') {
          searchAlertsDirect('');
        }
      }
    }
  }, [
    inputValue,
    unifiedSearchMode,
    isAskKlueEnabled,
    handleAskKlue,
    search,
    resetSuggestions,
    onSuggestionsClose,
    searchCardsDirect,
    searchAlertsDirect,
  ]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleDropdownToggle = () => {
    // Only allow dropdown toggle if there are multiple search options
    if (searchOptions.length > 1) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleSearchTypeChange = (searchType: UnifiedSearchMode) => {
    setUnifiedSearchMode(searchType);
    setIsDropdownOpen(false);

    // Close suggestions when switching modes
    setSuggestionOpen(false);
    resetSuggestions();

    // Focus and select text in search box for cards, alerts, and ask-klue (when not using initial screen)
    const shouldFocusAndSelect =
      searchType === 'cards' || searchType === 'alerts';

    if (shouldFocusAndSelect) {
      // Use setTimeout to ensure the input is ready after mode change
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 0);
    }

    // If there's a search term, handle search based on mode
    if (inputValue.trim()) {
      if (searchType === 'ask-klue' && isAskKlueEnabled) {
        // For ask-klue mode, execute the query and clear the input
        handleAskKlue(inputValue);
        setInputValue('');
      } else if (searchType === 'cards') {
        // For cards mode, use direct navigation
        searchCardsDirect(inputValue);
      } else if (searchType === 'alerts') {
        // For alerts mode, use direct navigation
        searchAlertsDirect(inputValue);
      }
    }
  };

  const searchOptions = useMemo(
    () => [
      ...(isAskKlueEnabled
        ? [
            {
              key: 'ask-klue' as UnifiedSearchMode,
              label: t('Common:askKlue.cta'),
              icon: Sparkle,
            },
          ]
        : []),
      ...(!isWinLossExclusive
        ? [
            {
              key: 'cards' as UnifiedSearchMode,
              label: t('Search:searchContext.cards'),
              icon: Cards,
            },
          ]
        : []),
      ...(!isWinLossExclusive && isCurator
        ? [
            {
              key: 'alerts' as UnifiedSearchMode,
              label: t('Search:searchContext.alerts'),
              icon: Bell,
            },
          ]
        : []),
    ],
    [isAskKlueEnabled, isWinLossExclusive, isCurator, t],
  );

  const currentOption = useMemo(
    () => searchOptions.find((option) => option.key === unifiedSearchMode),
    [searchOptions, unifiedSearchMode],
  );

  // If current mode is not in available options, switch to first available option
  useEffect(() => {
    if (!currentOption && searchOptions.length > 0) {
      setUnifiedSearchMode(searchOptions[0].key);
    }
  }, [currentOption, searchOptions, setUnifiedSearchMode]);

  const shouldShowArrowIcon =
    unifiedSearchMode === 'ask-klue' && isAskKlueEnabled;
  const shouldDisableInput =
    unifiedSearchMode === 'ask-klue' && isAskKlueEnabled;

  // Early return if no search options are available
  if (searchOptions.length === 0) {
    return null;
  }

  const getPlaceholder = () => {
    if (unifiedSearchMode === 'ask-klue') {
      return t('Common:askKlue.placeholder');
    }
    if (['cards', 'alerts'].includes(unifiedSearchMode)) {
      return '';
    }
    return t('Common:navBar.search.placeholder');
  };

  return (
    <>
      <StyledDropdownContainer ref={dropdownRef}>
        <StyledUnifiedSearchBox
          ref={searchBoxRef}
          align="center"
          direction="row"
          round="xsmall"
          justify="between"
          border={{ color: tokens.color.secondary.main, size: 'xsmall' }}
          pad={{ right: tokens.spacing.xxxsmall }}
          id="ask-klue-unified-search-box"
          style={{
            opacity: shouldBeHidden ? 0 : 1,
            visibility: shouldBeHidden ? 'hidden' : 'visible',
          }}
        >
          <Box
            background={tokens.color.secondary[100]}
            round="xsmall"
            direction="row"
            align="center"
            flex={{ grow: 1 }}
            gap="small"
            height="34px"
          >
            <Box
              direction="row"
              align="center"
              gap="xxsmall"
              pad={{ left: 'xsmall', vertical: 'xsmall' }}
              flex={{ shrink: 0 }}
              width={{ min: '100px' }}
            >
              <Box
                direction="row"
                align="center"
                gap="xxsmall"
                flex={{ grow: 1, shrink: 0 }}
                onClick={handleDropdownToggle}
                style={{
                  cursor: searchOptions.length > 1 ? 'pointer' : 'default',
                  minWidth: 'fit-content',
                }}
                pad={{ horizontal: 'xsmall' }}
                focusIndicator={false}
              >
                {currentOption && (
                  <>
                    {currentOption.key === 'ask-klue' ? (
                      <NectarIcon.KlueLogo
                        color={tokens.color.secondary[800]}
                        size={tokens.iconSize.large}
                      />
                    ) : (
                      <Text
                        color={tokens.color.secondary[800]}
                        fontWeight="medium"
                        size="medium"
                        truncate={false}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {currentOption.label}
                      </Text>
                    )}
                    {searchOptions.length > 1 && (
                      <Box margin={{ left: 'small' }}>
                        <CaretDown
                          color={tokens.color.secondary[800]}
                          size={tokens.iconSize.small}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
            <Box
              flex={{ grow: 1 }}
              background={tokens.color.neutral[100]}
              round="xsmall"
              pad={{ right: 'xxsmall' }}
              style={{
                borderTopLeftRadius: '0px',
                borderBottomLeftRadius: '0px',
              }}
            >
              <Box
                direction="row"
                align="center"
                gap="xxsmall"
                justify="between"
                pad={{ left: 'small', right: 'xsmall' }}
                height="33px"
              >
                <StyledUnifiedSearchInput
                  ref={inputRef}
                  plain="full"
                  size="small"
                  placeholder={getPlaceholder()}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  value={inputValue}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onClick={handleInputClick}
                  readOnly={shouldDisableInput}
                  style={{
                    cursor: shouldDisableInput ? 'pointer' : 'text',
                    backgroundColor: 'transparent',
                    padding: 0,
                    flex: 1,
                    minWidth: 0,
                  }}
                />
                <Box
                  flex={{ shrink: 0 }}
                  margin={{ left: 'xsmall' }}
                  onClick={shouldShowArrowIcon ? handleInputClick : undefined}
                  style={{
                    cursor: shouldShowArrowIcon ? 'pointer' : 'default',
                  }}
                >
                  {shouldShowArrowIcon && (
                    <ArrowSquareOut
                      color={tokens.color.neutral[700]}
                      size={tokens.iconSize.small}
                    />
                  )}
                  {!shouldShowArrowIcon && (
                    <MagnifyingGlass
                      color={tokens.color.neutral[700]}
                      size={tokens.iconSize.small}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </StyledUnifiedSearchBox>

        {isDropdownOpen && searchOptions.length > 1 && (
          <StyledDropdown $offset={dropdownOffset}>
            {searchOptions.map((option) => (
              <StyledDropdownItem
                key={option.key}
                onClick={() => handleSearchTypeChange(option.key)}
              >
                <Box direction="row" align="center" gap="small">
                  <option.icon
                    color={tokens.color.neutral[800]}
                    size={tokens.iconSize.small}
                  />
                  <Text color={tokens.color.neutral[800]} size="medium">
                    {option.label}
                  </Text>
                </Box>
              </StyledDropdownItem>
            ))}
          </StyledDropdown>
        )}
      </StyledDropdownContainer>

      {suggestionOpen && unifiedSearchMode !== 'ask-klue' && (
        <UnifiedSearchSuggestions
          inputRef={inputRef}
          onSuggestionsClose={onSuggestionsClose}
          suggestionItems={suggestionItems}
          searchContainerWidth={UNIFIED_SEARCH_EXPANDED_WIDTH}
        />
      )}
    </>
  );
};

export default NavBarUnifiedSearch;
